import React from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Typography,
  Divider,
  Space,
  Button
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Voucher, VoucherStatus, VoucherType } from '../../types';

const { Text, Title } = Typography;

interface VoucherDetailProps {
  voucher: Voucher | null;
  visible: boolean;
  onClose: () => void;
  onEdit?: (voucher: Voucher) => void;
  onDelete?: (voucher: Voucher) => void;
}

const VoucherDetail: React.FC<VoucherDetailProps> = ({
  voucher,
  visible,
  onClose,
  onEdit,
  onDelete
}) => {
  if (!voucher) return null;

  const getStatusColor = (status: VoucherStatus) => {
    switch (status) {
      case VoucherStatus.AVAILABLE: return 'green';
      case VoucherStatus.CLAIMED: return 'blue';
      case VoucherStatus.USED: return 'orange';
      case VoucherStatus.EXPIRED: return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status: VoucherStatus) => {
    switch (status) {
      case VoucherStatus.AVAILABLE: return 'Có sẵn';
      case VoucherStatus.CLAIMED: return 'Đã nhận';
      case VoucherStatus.USED: return 'Đã sử dụng';
      case VoucherStatus.EXPIRED: return 'Đã hết hạn';
      default: return status;
    }
  };

  const getTypeText = (type: VoucherType) => {
    switch (type) {
      case VoucherType.VOUCHER_10K: return '10K';
      case VoucherType.VOUCHER_20K: return '20K';
      case VoucherType.VOUCHER_50K: return '50K';
      case VoucherType.VOUCHER_100K: return '100K';
      case VoucherType.VOUCHER_200K: return '200K';
      case VoucherType.VOUCHER_300K: return '300K';
      case VoucherType.VOUCHER_500K: return '500K';
      case VoucherType.VOUCHER_1M: return '1M';
      case VoucherType.VOUCHER_2M: return '2M';
      default: return type;
    }
  };

  const formatValue = (value: number) => {
    return `${value.toLocaleString('vi-VN')} VNĐ`;
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>
            Chi tiết Voucher: {voucher.code}
          </Title>
          <Space>
            {onEdit && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => onEdit(voucher)}
              >
                Chỉnh sửa
              </Button>
            )}
            {onDelete && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(voucher)}
              >
                Xóa
              </Button>
            )}
          </Space>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label="Mã voucher" span={1}>
          <Text code style={{ fontSize: '16px' }}>{voucher.code}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Trạng thái" span={1}>
          <Tag color={getStatusColor(voucher.status)}>
            {getStatusText(voucher.status)}
          </Tag>
        </Descriptions.Item>
        
        <Descriptions.Item label="Loại voucher" span={1}>
          {getTypeText(voucher.voucher_type)}
        </Descriptions.Item>
        <Descriptions.Item label="Giá trị" span={1}>
          <Text strong>{formatValue(voucher.value)}</Text>
        </Descriptions.Item>
        
        <Descriptions.Item label="Mô tả" span={2}>
          {voucher.description || 'Không có mô tả'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Ngày tạo" span={1}>
          {dayjs(voucher.created_at).format('DD/MM/YYYY HH:mm')}
        </Descriptions.Item>
        <Descriptions.Item label="Ngày hết hạn" span={1}>
          {voucher.expires_at ? (
            <Text type={dayjs(voucher.expires_at).isBefore(dayjs()) ? 'danger' : 'secondary'}>
              {dayjs(voucher.expires_at).format('DD/MM/YYYY HH:mm')}
            </Text>
          ) : (
            <Text type="secondary">Không có</Text>
          )}
        </Descriptions.Item>
      </Descriptions>

      {voucher.user && (
        <>
          <Divider>Thông tin người dùng</Divider>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Tên" span={1}>
              {voucher.user.name}
            </Descriptions.Item>
            <Descriptions.Item label="Email" span={1}>
              {voucher.user.email || 'Chưa có'}
            </Descriptions.Item>
            <Descriptions.Item label="Số điện thoại" span={1}>
              {voucher.user.phone || 'Chưa có'}
            </Descriptions.Item>
            <Descriptions.Item label="Thời gian nhận" span={1}>
              {voucher.claimed_at ? dayjs(voucher.claimed_at).format('DD/MM/YYYY HH:mm') : 'Chưa nhận'}
            </Descriptions.Item>
            <Descriptions.Item label="Thời gian sử dụng" span={2}>
              {voucher.used_at ? dayjs(voucher.used_at).format('DD/MM/YYYY HH:mm') : 'Chưa sử dụng'}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </Modal>
  );
};

export default VoucherDetail;
